import {
	IconAdjustments,
	IconCircleCheckFilled,
	IconLayoutDashboard,
	IconLink,
	IconPalette,
	IconWorld,
} from "@tabler/icons-react"
import { type FC, useEffect, useMemo, useState } from "react"

import countries from "@forento/core/models/countries"
import { platformLanguageIds, platformLanguages } from "@forento/core/models/platform"
import { academyNameTrademarkNotice, getAcademyNameValidationError } from "@forento/core/utilities/academyName"
import { getDataUrlByFile } from "@forento/core/utilities/file"
import { hexColorRegex } from "@forento/core/utilities/regex"
import { replaceAll } from "@forento/core/utilities/string"
import { cn } from "@forento/ui/utilities/tailwind"

import { actionClass } from "~/components/Button"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import RichContentEditor from "~/components/RichContentEditor"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { useUser } from "~/contexts/UserContext"
import useFlagEmojiSupport from "~/hooks/useFlagEmojiSupport"
import { exportRichContent, parseRichContent, type RichContentValue } from "~/utilities/richContent"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import legacyThemeImage from "~/assets/themes/legacy.png"
import modernThemeImage from "~/assets/themes/modern.png"

import CurrencyModal from "./CurrencyModal"
import CustomEmailModal from "./CustomEmailModal"
import Preview from "./Preview"
import {
	AcademyImageUpload,
	fieldClass,
	SettingSwitch,
	SettingsPanel,
	SettingsRow,
	SettingsSection,
} from "./SettingsControls"
import SlugModal from "./SlugModal"

const CustomizeAcademyPage: FC = () => {
	const [isLogoDisplaySaving, setLogoDisplaySaving] = useState(false)
	const [isFaviconImporting, setFaviconImporting] = useState(false)
	const user = useUser()
	const alert = useAlert()
	const toast = useToast()
	const flagEmojiSupported = useFlagEmojiSupport()

	const platform = user.user!.platform!

	const welcomeMessageQuery = query.platform.getWelcomeMessage.useQuery()

	const [name, setName] = useState(platform.name)
	const [title, setTitle] = useState(platform.title ?? "")
	const [description, setDescription] = useState(platform.description ?? "")
	const [language, setLanguage] = useState(platform.language)
	const [isSignupsOpen, setSignupsOpen] = useState(platform.isSignupsOpen)
	const [isPhoneNumberEnabled, setPhoneNumberEnabled] = useState(platform.phoneNumberCollection !== null)
	const [defaultPhoneNumberCountry, setDefaultPhoneNumberCountry] = useState(
		platform.phoneNumberCollection?.defaultCountry ?? "NONE",
	)
	const detectedCountryLabel = countries.find(country => country.code === platform.detectedCountry)?.label
	const [primaryColor, setPrimaryColor] = useState(platform.primaryColor.isCustom ? platform.primaryColor.value : "")
	const [welcomeMessage, setWelcomeMessage] = useState<RichContentValue | null>(null)

	const [isSlugModalOpen, setSlugModalOpen] = useState(false)
	const [isCurrencyModalOpen, setCurrencyModalOpen] = useState(false)
	const [isCustomEmailModalOpen, setCustomEmailModalOpen] = useState(false)

	const update = query.platform.update.useMutation({
		async onSuccess() {
			await Promise.all([user.reload(), welcomeMessageQuery.refetch()])
		},
	})

	const setFavicon = query.platform.setFavicon.useMutation({
		async onSuccess() {
			await user.reload()
			toast.showChangesSaved("Favicon")
		},
	})
	const deleteFavicon = query.platform.deleteFavicon.useMutation({
		async onSuccess() {
			await user.reload()
			toast.showChangesSaved("Favicon")
		},
	})

	const initialWelcomeMessage = useMemo(
		() => parseRichContent(welcomeMessageQuery.data ?? null),
		[welcomeMessageQuery.data],
	)

	useEffect(() => {
		const isModified =
			name.trim() !== platform.name ||
			title.trim() !== (platform.title ?? "") ||
			description.trim() !== (platform.description ?? "") ||
			isSignupsOpen !== platform.isSignupsOpen ||
			isPhoneNumberEnabled !== (platform.phoneNumberCollection !== null) ||
			(isPhoneNumberEnabled &&
				defaultPhoneNumberCountry !== (platform.phoneNumberCollection?.defaultCountry ?? "NONE")) ||
			(primaryColor === "#" ? "" : primaryColor) !==
				(platform.primaryColor.isCustom ? platform.primaryColor.value : "") ||
			language !== platform.language ||
			welcomeMessage?.isModified

		if (!isModified) {
			toast.clearUnsavedChanges()
		} else {
			toast.setUnsavedChanges(async () => {
				const hasPrimaryColor = primaryColor.length > 0 && primaryColor !== "#"
				if (hasPrimaryColor) {
					if (!hexColorRegex.test(primaryColor)) {
						await alert.show(
							"Invalid primary color",
							"Invalid primary color. It should be a hex value in the format #XXXXXX.",
						)
						return false
					}
				}

				const academyNameError = getAcademyNameValidationError(name)
				if (academyNameError !== null) {
					await alert.show("Invalid academy name", academyNameError)
					return false
				}

				if (platform.phoneNumberCollection !== null && !isPhoneNumberEnabled) {
					const result = await alert.confirm(
						"Disable phone number collection",
						"You are about to disable phone number collection. This will remove all phone numbers from your academy. Are you sure you want to continue?",
					)
					if (!result.result) return false
					result.close()
				}

				try {
					await update.mutateAsync({
						name,
						title,
						description,
						isSignupsOpen,
						isPhoneNumberEnabled,
						defaultPhoneNumberCountry:
							defaultPhoneNumberCountry !== "NONE"
								? countries.find(x => x.code === defaultPhoneNumberCountry)?.code
								: null,
						primaryColor: hasPrimaryColor ? primaryColor : null,
						language,
						welcomeMessage: welcomeMessage?.isModified ? exportRichContent(welcomeMessage) : undefined,
					})
					setWelcomeMessage(null)
					return true
				} catch (error) {
					console.error(error)
				}

				return false
			})
		}
	}, [
		alert,
		description,
		isPhoneNumberEnabled,
		isSignupsOpen,
		language,
		name,
		platform.description,
		platform.phoneNumberCollection,
		defaultPhoneNumberCountry,
		platform.isSignupsOpen,
		platform.language,
		platform.name,
		platform.primaryColor.isCustom,
		platform.primaryColor.value,
		platform.title,
		primaryColor,
		title,
		toast,
		user,
		update,
		welcomeMessage,
	])

	const handleUpdateLogo = async (file: File) => {
		await trpc.platform.setLogo.mutate({ dataUrl: await getDataUrlByFile(file) })
		await user.reload()
		toast.showChangesSaved("Logo")
	}

	const handleDeleteLogo = async () => {
		await trpc.platform.deleteLogo.mutate()
		await user.reload()
		toast.showChangesSaved("Logo")
	}

	const handleUpdateThumbnail = async (file: File) => {
		await trpc.platform.setThumbnail.mutate({ dataUrl: await getDataUrlByFile(file) })
		await user.reload()
		toast.showChangesSaved("Thumbnail")
	}

	const handleDeleteThumbnail = async () => {
		await trpc.platform.deleteThumbnail.mutate()
		await user.reload()
		toast.showChangesSaved("Thumbnail")
	}

	return (
		<Layout>
			<SlugModal isOpen={isSlugModalOpen} onClose={() => setSlugModalOpen(false)} />
			<CurrencyModal isOpen={isCurrencyModalOpen} onClose={() => setCurrencyModalOpen(false)} />
			<CustomEmailModal isOpen={isCustomEmailModalOpen} setOpen={setCustomEmailModalOpen} />
			<div className="max-w-4xl pb-8">
				<PageBreadcrumb
					title="Customize Academy"
					path={[{ title: "Settings", link: routes.settings.index() }]}
				/>
				<div className="border-component-border bg-component-background mb-5 flex items-center gap-4 rounded-2xl border px-5 py-4">
					<div className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-xl">
						<IconPalette size={24} stroke={1.6} aria-hidden="true" />
					</div>
					<div>
						<h2 className="text-xl font-semibold tracking-tight">Make your academy your own</h2>
						<p className="mt-1 text-sm text-gray-500">
							Fine-tune your brand, student experience, and how you appear online.
						</p>
					</div>
				</div>
				<div className="divide-component-border border-component-border divide-y overflow-hidden rounded-2xl border bg-white">
					<SettingsPanel
						title="Academy details"
						description="Name, language, and student registration."
						icon={<IconAdjustments size={20} stroke={1.6} aria-hidden="true" />}
					>
						<SettingsSection>
							<SettingsRow
								label="Academy name"
								description={academyNameTrademarkNotice}
								htmlFor="academy-name"
							>
								<input
									id="academy-name"
									className={fieldClass}
									value={name}
									onChange={event => setName(event.target.value)}
								/>
							</SettingsRow>
							<SettingsRow
								label="Language"
								description="The language students see in your academy."
								htmlFor="academy-language"
							>
								<select
									id="academy-language"
									className={fieldClass}
									value={language}
									onChange={event =>
										setLanguage(platformLanguageIds.find(id => id === event.target.value) ?? "en")
									}
								>
									{Object.entries(platformLanguages).map(([id, { flag, label }]) => (
										<option key={id} value={id}>
											{flagEmojiSupported ? `${flag} ${label}` : label}
										</option>
									))}
								</select>
							</SettingsRow>
						</SettingsSection>
						<SettingsSection title="Student experience">
							{platform.enabledFeatures.includes("block-signups") && (
								<SettingsRow
									label="Open registration"
									description="Allow students to sign up without an invitation."
								>
									<SettingSwitch
										label="Allow non-invited users to sign up"
										checked={isSignupsOpen}
										onChange={setSignupsOpen}
									/>
								</SettingsRow>
							)}
							<SettingsRow label="Phone numbers" description="Collect student phone numbers.">
								<SettingSwitch
									label="Enable phone number collection"
									checked={isPhoneNumberEnabled}
									onChange={setPhoneNumberEnabled}
								/>
							</SettingsRow>
							{isPhoneNumberEnabled && (
								<SettingsRow
									label="Default country"
									description="Preselected country for phone numbers."
									htmlFor="phone-country"
								>
									<select
										id="phone-country"
										className={fieldClass}
										value={defaultPhoneNumberCountry}
										onChange={event => setDefaultPhoneNumberCountry(event.target.value)}
									>
										<option value="NONE">
											{detectedCountryLabel !== undefined
												? `Automatic (${detectedCountryLabel})`
												: "No default country"}
										</option>
										{countries.map(country => (
											<option key={country.code} value={country.code}>
												{country.label}
											</option>
										))}
									</select>
								</SettingsRow>
							)}
						</SettingsSection>
					</SettingsPanel>
					<SettingsPanel
						title="Welcome message"
						description="Shown at the top of the Overview page, including to visitors who aren’t signed in. Use the video buttons in the toolbar to upload a video or add one from YouTube or Vimeo."
						icon={<IconLayoutDashboard size={20} stroke={1.6} aria-hidden="true" />}
					>
						{welcomeMessageQuery.error ? (
							<p className="text-sm text-gray-500">Failed to load the welcome message.</p>
						) : welcomeMessageQuery.data === undefined ? (
							<div className="border-component-border bg-component-background min-h-64 w-full animate-pulse rounded-xl border" />
						) : (
							<RichContentEditor
								placeholder="Welcome your members with a video and a few words…"
								initialValue={initialWelcomeMessage}
								setValue={setWelcomeMessage}
							/>
						)}
					</SettingsPanel>
					<SettingsPanel
						title="Brand & appearance"
						description="Your logo, colors, and academy theme."
						icon={<IconPalette size={20} stroke={1.6} aria-hidden="true" />}
					>
						<SettingsSection
							title="Brand identity"
							description="Images and display options save automatically."
						>
							<SettingsRow
								label="Brand color"
								description="Buttons and accents in your academy."
								htmlFor="brand-color"
							>
								<div className="flex items-center gap-2">
									<input
										type="color"
										aria-label="Choose brand color"
										value={hexColorRegex.test(primaryColor) ? primaryColor : "#A155F5"}
										onChange={event => setPrimaryColor(event.target.value)}
										className="size-9 shrink-0 cursor-pointer rounded-md border border-stone-300 bg-white p-1"
									/>
									<input
										id="brand-color"
										className={cn(fieldClass, "max-w-36 font-mono")}
										placeholder="Default"
										value={primaryColor}
										onChange={event =>
											setPrimaryColor(
												event.target.value
													? `#${replaceAll(event.target.value, "#", "").trim().substring(0, 6)}`
													: "",
											)
										}
									/>
									<button
										type="button"
										onClick={() => setPrimaryColor("")}
										className="rounded px-2 py-2 text-xs text-gray-500 underline underline-offset-2 hover:text-gray-900 focus-visible:outline-2"
									>
										Reset
									</button>
								</div>
							</SettingsRow>
							<SettingsRow label="Logo" description="Your academy’s logo in the navigation.">
								<AcademyImageUpload
									label="Logo"
									hint="128 px tall · drag & drop"
									filePath={platform.logo?.filePath ?? null}
									onUpload={handleUpdateLogo}
									onDelete={handleDeleteLogo}
								/>
								{platform.logo && (
									<div className="mt-3">
										<label htmlFor="logo-display" className="mb-1.5 block text-xs text-gray-500">
											Display
										</label>
										<select
											id="logo-display"
											className={fieldClass}
											value={platform.logo.isIcon ? "icon" : "logo"}
											disabled={isLogoDisplaySaving}
											onChange={async event => {
												setLogoDisplaySaving(true)
												try {
													await trpc.platform.update.mutate({
														isLogoIcon: event.target.value === "icon",
													})
													await user.reload()
													toast.showChangesSaved("Logo")
												} catch {
													await alert.show("Could not save logo display", "Please try again.")
												} finally {
													setLogoDisplaySaving(false)
												}
											}}
										>
											<option value="logo">Logo only</option>
											<option value="icon">Logo and academy name</option>
										</select>
									</div>
								)}
							</SettingsRow>
							<SettingsRow label="Favicon" description="The small icon shown in browser tabs.">
								<AcademyImageUpload
									label="Favicon"
									hint="128 × 128 px · drag & drop"
									filePath={platform.faviconPath}
									onUpload={async file =>
										setFavicon.mutateAsync({ dataUrl: await getDataUrlByFile(file) })
									}
									onDelete={() => deleteFavicon.mutateAsync()}
								/>
								{platform.logo?.isIcon && (
									<button
										type="button"
										disabled={setFavicon.isPending || isFaviconImporting}
										className="text-primary mt-2 rounded py-1 text-xs underline underline-offset-2 disabled:opacity-50"
										onClick={async () => {
											setFaviconImporting(true)
											try {
												const response = await fetch(platform.logo!.filePath)
												if (!response.ok) throw new Error("Could not load logo")
												const file = await response.blob()
												await setFavicon.mutateAsync({
													dataUrl: await getDataUrlByFile(
														new File([file], "Favicon", { type: file.type }),
													),
												})
											} catch {
												await alert.show("Could not import logo", "Please try again.")
											} finally {
												setFaviconImporting(false)
											}
										}}
									>
										{isFaviconImporting ? "Importing…" : "Use uploaded logo"}
									</button>
								)}
							</SettingsRow>
						</SettingsSection>
						<SettingsSection title="Theme" description="Theme changes save automatically.">
							<div className="grid grid-cols-2 gap-3 py-4">
								{[
									{ id: "modern" as const, name: "Modern", image: modernThemeImage },
									{ id: "legacy" as const, name: "Legacy", image: legacyThemeImage },
								].map(theme => (
									<button
										key={theme.id}
										type="button"
										aria-pressed={platform.theme.id === theme.id}
										disabled={update.isPending}
										className={cn(
											"focus-visible:outline-primary overflow-hidden rounded-md border text-left transition focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait disabled:opacity-60",
											platform.theme.id === theme.id
												? "border-primary ring-primary ring-1"
												: "border-component-border hover:border-stone-400",
										)}
										onClick={async () => {
											if (platform.theme.id === theme.id) return
											try {
												await update.mutateAsync({ themeId: theme.id })
												toast.showChangesSaved("Theme")
											} catch {
												await alert.show("Could not change theme", "Please try again.")
											}
										}}
									>
										<img
											src={theme.image}
											alt={`${theme.name} theme preview`}
											className="border-component-border aspect-[2.7/1] w-full border-b object-cover object-top"
										/>
										<span className="flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium">
											<span>{theme.name}</span>
											{platform.theme.id === theme.id && (
												<IconCircleCheckFilled
													size={17}
													className="text-primary shrink-0"
													aria-label="Selected"
												/>
											)}
										</span>
									</button>
								))}
							</div>
							{platform.theme.id === "modern" && (
								<SettingsRow
									label="Course background"
									description="Show the background on course pages."
								>
									<SettingSwitch
										label="Show course page background"
										checked={platform.theme.showCoursePageBackground}
										disabled={update.isPending}
										onChange={async checked => {
											try {
												await update.mutateAsync({ showCoursePageBackground: checked })
												toast.showChangesSaved("Theme")
											} catch {
												await alert.show("Could not save theme setting", "Please try again.")
											}
										}}
									/>
								</SettingsRow>
							)}
						</SettingsSection>
					</SettingsPanel>
					<SettingsPanel
						title="Search & sharing"
						description="How your academy looks in search results and shared links."
						icon={<IconWorld size={20} stroke={1.6} aria-hidden="true" />}
					>
						<SettingsSection>
							<SettingsRow
								label="Page title"
								description="Leave blank to use your academy name."
								htmlFor="sharing-title"
							>
								<input
									id="sharing-title"
									className={fieldClass}
									value={title}
									placeholder={name}
									onChange={event => setTitle(event.target.value)}
								/>
							</SettingsRow>
							<SettingsRow
								label="Description"
								description="A short introduction to your academy."
								htmlFor="sharing-description"
							>
								<textarea
									id="sharing-description"
									className={cn(fieldClass, "min-h-24 resize-y")}
									rows={3}
									value={description}
									onChange={event => setDescription(event.target.value)}
								/>
							</SettingsRow>
							<SettingsRow
								label="Thumbnail"
								description="Shown in shared links and throughout your academy. Uploads save automatically."
							>
								<AcademyImageUpload
									label="Thumbnail"
									hint="1200 × 630 px · drag & drop"
									filePath={platform.thumbnailFilePath}
									onUpload={handleUpdateThumbnail}
									onDelete={handleDeleteThumbnail}
								/>
							</SettingsRow>
							<SettingsRow label="Link preview" description="Appearance varies by platform.">
								<Preview
									title={title.trim() || name.trim()}
									description={description}
									imageFileUrl={platform.thumbnailFilePath ?? ""}
									domain={`${platform.slug}.forento.app`}
								/>
							</SettingsRow>
						</SettingsSection>
					</SettingsPanel>
					<SettingsPanel
						title="Connections"
						description="Academy address, currency, and email sender."
						icon={<IconLink size={20} stroke={1.6} aria-hidden="true" />}
					>
						<SettingsSection>
							<SettingsRow label="Domain slug" description="Your default academy address.">
								<div className="flex flex-wrap items-center justify-between gap-3">
									<p className="min-w-0 py-1.5 text-sm break-all text-gray-600">
										{platform.slug}.forento.app
									</p>
									<button
										type="button"
										className={actionClass}
										onClick={() => setSlugModalOpen(true)}
									>
										Change
									</button>
								</div>
							</SettingsRow>
							{platform.paymentGateway?.isEnabled && (
								<SettingsRow label="Currency" description="The currency used for your products.">
									<div className="flex items-center justify-between gap-3">
										<span className="text-sm font-medium text-gray-700">{platform.currency}</span>
										<button
											type="button"
											className={actionClass}
											onClick={() => setCurrencyModalOpen(true)}
										>
											Change
										</button>
									</div>
								</SettingsRow>
							)}
							<SettingsRow label="Email sender" description="Send academy emails from your own domain.">
								<div className="flex flex-wrap items-center justify-between gap-3">
									<div className="min-w-0">
										<span
											className={cn(
												"inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium",
												platform.customEmailSender?.isVerified
													? "bg-emerald-50 text-emerald-700"
													: platform.customEmailSender
														? "bg-amber-50 text-amber-700"
														: "bg-component-background text-gray-500",
											)}
										>
											<span className="size-1.5 rounded-full bg-current" />
											{platform.customEmailSender?.isVerified
												? "Verified"
												: platform.customEmailSender
													? "Pending verification"
													: "Not connected"}
										</span>
										{platform.customEmailSender && (
											<p className="mt-1.5 text-xs break-all text-gray-500">
												{platform.customEmailSender.domain}
											</p>
										)}
									</div>
									<button
										type="button"
										className={actionClass}
										onClick={() => setCustomEmailModalOpen(true)}
									>
										{platform.customEmailSender ? "Manage" : "Connect"}
									</button>
								</div>
							</SettingsRow>
						</SettingsSection>
					</SettingsPanel>
				</div>
			</div>
		</Layout>
	)
}

export default CustomizeAcademyPage
